import { useCallback } from "react"
import { BridgeTransactionHistory } from "@mezo/api"
import { getTokenName } from "@mezo/sdk"
import { useQuery } from "@tanstack/react-query"
import { Address } from "viem"

import {
  selectEvmAddress,
  setBridgeHistory,
  setHasLoadedBridgeData,
  setLatestBridgeTransaction,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { DATABASE_DECIMALS_COUNT, PORTAL_API_URL_V2 } from "#/shared/constants"

const useBridgingHistory = () => {
  const address = useDappSelector(selectEvmAddress)
  const dispatch = useDappDispatch()

  const { refetch: fetchBridgeHistory } = useQuery<BridgeTransactionHistory[]>({
    enabled: !!address,
    queryKey: ["bridgeHistory"],
    queryFn: async () => {
      const response = await fetch(
        `${PORTAL_API_URL_V2}bridge/${address}/history`,
        { credentials: "include" },
      )

      if (!response.ok) return []

      const bridgeHistory = await response.json<BridgeTransactionHistory[]>()

      if (bridgeHistory) {
        dispatch(setBridgeHistory(bridgeHistory))
      }

      dispatch(setHasLoadedBridgeData(true))

      return bridgeHistory
    },
  })

  const setLatestBridgeTransactionHandler = useCallback(
    (
      tokenAmount: bigint,
      tokenAddress: Address,
      tokenDecimals: number,
      transactionHash: string,
    ) => {
      dispatch(
        setLatestBridgeTransaction({
          amount: (
            tokenAmount *
            10n ** BigInt(DATABASE_DECIMALS_COUNT - tokenDecimals)
          ).toString(),
          token: getTokenName(tokenAddress)!,
          transactionHash,
          timestamp: Date.now(),
        }),
      )
    },
    [dispatch],
  )

  return {
    fetchBridgeHistory,
    setLatestBridgeTransaction: setLatestBridgeTransactionHandler,
  }
}

export default useBridgingHistory
